import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';

interface AskQuantQuery {
  id: string;
  question: string;
  answer: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
  error?: string;
}

interface AskQuantProps {
  limit?: number;
}

export function AskQuant({ limit = 1 }: AskQuantProps) {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [queries, setQueries] = useState<AskQuantQuery[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const loadRecentQueries = useCallback(async () => {
    try {
      // Query the database for recent queries
      const { data: queriesData, error } = await supabase
        .from('ask_quant_queries')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error loading recent queries:', error);
        return;
      }

      setQueries(queriesData || []);
    } catch (error) {
      console.error('Error loading recent queries:', error);
    }
  }, [limit, supabase]);

  const handleSubmit = async () => {
    if (!question.trim()) return;
    if (!user?.id) {
      setError('You must be logged in to ask questions');
      return;
    }

    setSubmitting(true);
    setError(null);
    let newQuery: any = null;

    try {
      // Create a new query record in the database
      const { data: queryData, error: createError } = await supabase
        .from('ask_quant_queries')
        .insert({
          question: question.trim(),
          status: 'pending',
          user_id: user.id
        })
        .select()
        .single();

      if (createError) {
        throw new Error(`Failed to create query: ${createError.message}`);
      }

      newQuery = queryData;

      // Add to local state immediately
      setQueries(prev => [newQuery, ...prev.slice(0, limit - 1)]);

      // Clear the input
      setQuestion('');

      // Trigger the background search
      const response = await fetch('/.netlify/functions/ask-quant-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryId: newQuery.id,
          question: newQuery.question,
        }),
      });

      // Handle different response status codes
      if (response.status === 202) {
        // 202 Accepted - background processing started
        console.log('Background processing started for query:', newQuery.id);
      } else if (!response.ok) {
        throw new Error(`Failed to submit question: ${response.status} ${response.statusText}`);
      } else {
        // Parse response for other status codes
        let result;
        try {
          const responseText = await response.text();
          console.log('Response text:', responseText);
          
          if (!responseText || responseText.trim() === '') {
            throw new Error('Empty response from server');
          }
          
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          console.error('Response status:', response.status);
          console.error('Response headers:', Object.fromEntries(response.headers.entries()));
          throw new Error('Invalid response from server');
        }
        
        if (result.error) {
          throw new Error(result.error);
        }
      }

      // Update query status to processing
      setQueries(prev => prev.map(q => 
        q.id === newQuery.id 
          ? { ...q, status: 'processing' as const }
          : q
      ));

      // Start polling for results
      pollForResults(newQuery.id);

    } catch (err) {
      console.error('Error submitting question:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit question');
      
      // Update query status to failed
      if (newQuery) {
        setQueries(prev => prev.map(q => 
          q.id === newQuery.id 
            ? { ...q, status: 'failed' as const, error: err instanceof Error ? err.message : 'Unknown error' }
            : q
        ));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pollForResults = useCallback(async (queryId: string) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setQueries(prev => prev.map(q => 
          q.id === queryId 
            ? { ...q, status: 'failed' as const, error: 'Timeout waiting for results' }
            : q
        ));
        return;
      }

      try {
        const response = await fetch(`/.netlify/functions/check-ask-quant-status?queryId=${queryId}`);
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.status === 'completed' && result.answer) {
            // Update with the answer
            setQueries(prev => prev.map(q => 
              q.id === queryId 
                ? { 
                    ...q, 
                    status: 'completed' as const, 
                    answer: result.answer,
                    completed_at: new Date().toISOString()
                  }
                : q
            ));

            // Reload queries from database to get updated data
            await loadRecentQueries();
            return;
          } else if (result.status === 'failed') {
            setQueries(prev => prev.map(q => 
              q.id === queryId 
                ? { ...q, status: 'failed' as const, error: result.error || 'Processing failed' }
                : q
            ));
            return;
          }
        }

        attempts++;
        setTimeout(poll, 2000); // Poll every 2 seconds
      } catch (error) {
        console.error('Error polling for results:', error);
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
  }, [loadRecentQueries]);

  const handleDeleteQuery = async (queryId: string) => {
    try {
      // Delete the query from the database
      const { error } = await supabase
        .from('ask_quant_queries')
        .delete()
        .eq('id', queryId);

      if (error) {
        console.error('Error deleting query:', error);
        setError('Failed to delete question');
        return;
      }

      // Remove from local state
      setQueries(prev => prev.filter(q => q.id !== queryId));
    } catch (err) {
      console.error('Error deleting query:', err);
      setError('Failed to delete question');
    }
  };

  React.useEffect(() => {
    loadRecentQueries();
  }, [loadRecentQueries]);

  // Resume polling for any queries that are still processing
  React.useEffect(() => {
    const processingQueries = queries.filter(q => q.status === 'processing');
    processingQueries.forEach(query => {
      pollForResults(query.id);
    });
  }, [queries, pollForResults]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!submitting && question.trim()) {
        handleSubmit();
      }
    }
  };

  return (
    <Box sx={{ 
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      p: 1.5,
      gridColumn: { xs: '1', sm: 'span 1' }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PsychologyIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Ask Quant
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Input Section */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask a question about your data, market trends, or get insights..."
          variant="outlined"
          size="small"
          disabled={submitting}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!question.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              borderRadius: 1,
              px: 2,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {submitting ? 'Asking...' : 'Ask'}
          </Button>
        </Box>
      </Box>

        {/* Recent Queries */}
        {queries.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
            Last Question
          </Typography>
        <List sx={{ p: 0 }}>
          {queries.map((query, idx) => (
            <React.Fragment key={query.id}>
                <ListItem sx={{ 
                  py: 1, 
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 0.5
                }}>
                  {/* Question */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.primary', 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      mb: 0.5
                    }}
                  >
                    {query.question}
                  </Typography>

                  {/* Status and Actions */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    gap: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {query.status === 'pending' && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          Queued for processing...
                        </Typography>
                      )}

                      {query.status === 'processing' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={12} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Processing...
                          </Typography>
                        </Box>
                      )}

                      {query.status === 'completed' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            // Navigate to reports page or open modal
                            window.location.href = '/reports';
                          }}
                          sx={{
                            fontSize: '0.7rem',
                            py: 0.25,
                            px: 1,
                            minWidth: 'auto',
                            height: 24
                          }}
                        >
                          View Report
                        </Button>
                      )}

                      {query.status === 'failed' && (
                        <Typography variant="caption" sx={{ color: 'error.main', fontStyle: 'italic' }}>
                          Error: {query.error || 'Processing failed'}
                        </Typography>
                      )}
                    </Box>

                    {/* Delete Button */}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleDeleteQuery(query.id)}
                      sx={{
                        minWidth: 'auto',
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: 'error.light'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </Button>
                  </Box>

                </ListItem>
                {idx < queries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}